import React, { useEffect, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface Props {
  title: string;
  // Compact content shown in the header while collapsed (and above the body when open).
  summary?: React.ReactNode;
  // When set, the open/closed state is remembered in localStorage under this key.
  storageKey?: string;
  defaultOpen?: boolean;
  id?: string;
  children: React.ReactNode;
}

const readStored = (key: string | undefined, fallback: boolean): boolean => {
  if (!key) return fallback;
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
};

const writeStored = (key: string | undefined, open: boolean) => {
  if (!key) return;
  try {
    window.localStorage.setItem(key, open ? '1' : '0');
  } catch {
    /* storage unavailable (private window, blocked site data) — state is just not remembered */
  }
};

// A page card whose body can be folded away. Matches the plain card styling used across
// CaseDetail so it can wrap an existing section without a visual change when open.
const CollapsibleCard: React.FC<Props> = ({ title, summary, storageKey, defaultOpen = false, id, children }) => {
  const [open, setOpen] = useState(() => readStored(storageKey, defaultOpen));
  const toggle = () => {
    setOpen((o) => {
      writeStored(storageKey, !o);
      return !o;
    });
  };
  const bodyId = id ? `${id}-body` : undefined;

  // Let a page-level jump (e.g. from an AI finding) open this card before scrolling to it.
  useEffect(() => {
    if (!id) return;
    const handler = (e: Event) => {
      if ((e as CustomEvent<string>).detail === id) {
        setOpen(true);
        writeStored(storageKey, true);
      }
    };
    window.addEventListener('ipri:expand-section', handler);
    return () => window.removeEventListener('ipri:expand-section', handler);
  }, [id, storageKey]);

  return (
    <div id={id} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          {summary && <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">{summary}</div>}
        </div>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          {open ? 'Hide' : 'Show'}
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div id={bodyId} className="mt-4">
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleCard;
